import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Check, Globe, Star, MapPin, Users, Link2, BadgeCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MarketingLayout, PageHero, SectionHeader } from "@/components/marketing/Layout";
import { Logo } from "@/components/marketing/Logo";
import { buildSeo } from "@/lib/seo";

export const Route = createFileRoute("/nuvatr")({
  head: () =>
    buildSeo({
      path: "/nuvatr",
      title: "Nuvatr × Reality Twin — global registry partnership",
      description:
        "Reality Twin partners with Nuvatr on a global company identifier. Subscribers get 5 CHF listings (vs 25 CHF) and up to 30% off Nuvatr services.",
    }),
  component: NuvatrPage,
});

const ICONS = [BadgeCheck, Globe, Link2, Star, MapPin, Users];

function NuvatrPage() {
  const { t } = useTranslation();
  const features = t("nuvatr.deliver.features", { returnObjects: true }) as { title: string; desc: string }[];
  const listingBullets = t("nuvatr.benefits.listing.bullets", { returnObjects: true }) as string[];
  const discountBullets = t("nuvatr.benefits.discount.bullets", { returnObjects: true }) as string[];
  const steps = t("nuvatr.how.steps", { returnObjects: true }) as { n: string; title: string; desc: string }[];

  return (
    <MarketingLayout>
      <PageHero
        eyebrow={t("nuvatr.hero.eyebrow")}
        title={
          <>
            {t("nuvatr.hero.titlePre")} <span className="italic text-muted-foreground">×</span> {t("nuvatr.hero.titleAfter")}
          </>
        }
        sub={t("nuvatr.hero.sub")}
      />

      {/* CO-BRAND BLOCK */}
      <section className="border-b border-border bg-cream py-16">
        <div className="mx-auto max-w-5xl px-6">
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:justify-center sm:gap-10">
            <Logo size={22} />
            <div className="text-muted-foreground">×</div>
            <a
              href="https://nuvatr.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 hover:opacity-70 transition"
              aria-label="Visit Nuvatr"
            >
              <span className="font-bold tracking-[0.18em] text-[20px]">NUVATR</span>
            </a>
          </div>
          <p className="mt-8 text-center text-[14px] text-muted-foreground max-w-2xl mx-auto">
            {t("nuvatr.cobrand.taglinePre")} <a href="https://ruznic-marketing.com" className="underline hover:text-foreground">Ruznic Marketing</a>{t("nuvatr.cobrand.taglinePost")}
          </p>
        </div>
      </section>

      {/* WHAT IS NUVATR */}
      <section className="border-b border-border py-20">
        <div className="mx-auto max-w-3xl px-6 space-y-5 text-[16.5px] leading-[1.7] text-muted-foreground">
          <div className="font-mono-eyebrow text-foreground">{t("nuvatr.what.eyebrow")}</div>
          <h2 className="font-serif text-[40px] sm:text-[48px] leading-[1.02] tracking-[-0.025em] text-foreground">
            {t("nuvatr.what.titleA")} <em className="italic text-muted-foreground">{t("nuvatr.what.titleEm")}</em>
          </h2>
          <p>{t("nuvatr.what.p1")}</p>
          <p>
            {t("nuvatr.what.p2Pre")} <strong className="text-foreground">{t("nuvatr.what.gtnLabel")}</strong> {t("nuvatr.what.p2Post")}
          </p>
          <p>{t("nuvatr.what.p3")}</p>
        </div>
      </section>

      {/* WHAT NUVATR DELIVERS */}
      <section className="border-b border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("nuvatr.deliver.eyebrow")}
            title={
              <>
                {t("nuvatr.deliver.titleA")} <em className="italic text-muted-foreground">{t("nuvatr.deliver.titleEm")}</em>
              </>
            }
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border hairline rounded-xl overflow-hidden">
            {features.map((f, i) => {
              const Icon = ICONS[i] ?? BadgeCheck;
              return (
                <div key={f.title} className="bg-card p-7">
                  <Icon className="h-5 w-5 text-foreground" strokeWidth={1.5} />
                  <h3 className="mt-4 font-serif text-[20px] leading-tight">{f.title}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-muted-foreground">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* THE PARTNERSHIP — DISCOUNTS */}
      <section className="bg-foreground text-background py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="max-w-3xl">
            <div className="font-mono-eyebrow text-background/60">{t("nuvatr.benefits.eyebrow")}</div>
            <h2 className="mt-3 font-serif text-[40px] sm:text-[56px] leading-[1.02] tracking-[-0.025em]">
              {t("nuvatr.benefits.titleA")} <em className="italic text-background/70">{t("nuvatr.benefits.titleEm")}</em>
            </h2>
            <p className="mt-5 text-[16.5px] leading-[1.6] text-background/70 max-w-xl">
              {t("nuvatr.benefits.sub")}
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-px bg-background/20 rounded-xl overflow-hidden">
            <div className="bg-foreground p-8 sm:p-10">
              <div className="font-mono-eyebrow text-background/60">{t("nuvatr.benefits.listing.eyebrow")}</div>
              <div className="mt-4 flex items-baseline gap-3">
                <div className="font-serif text-[64px] leading-none tracking-[-0.04em]">CHF 5</div>
                <div className="text-background/50 line-through text-[18px]">CHF 25</div>
              </div>
              <p className="mt-4 text-[15px] text-background/70 leading-relaxed max-w-md">
                {t("nuvatr.benefits.listing.desc")}
              </p>
              <ul className="mt-6 space-y-2.5 text-[14px] text-background/80">
                {listingBullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-background" strokeWidth={2} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-foreground p-8 sm:p-10">
              <div className="font-mono-eyebrow text-background/60">{t("nuvatr.benefits.discount.eyebrow")}</div>
              <div className="mt-4 flex items-baseline gap-3">
                <div className="font-serif text-[64px] leading-none tracking-[-0.04em]">−30%</div>
                <div className="text-background/50 text-[18px]">{t("nuvatr.benefits.discount.on")}</div>
              </div>
              <p className="mt-4 text-[15px] text-background/70 leading-relaxed max-w-md">
                {t("nuvatr.benefits.discount.desc")}
              </p>
              <ul className="mt-6 space-y-2.5 text-[14px] text-background/80">
                {discountBullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-background" strokeWidth={2} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-8 text-[12.5px] text-background/50">
            {t("nuvatr.benefits.footnote")}
          </p>
        </div>
      </section>

      {/* HOW THE SYNC WORKS */}
      <section className="border-b border-border py-24">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            eyebrow={t("nuvatr.how.eyebrow")}
            title={
              <>
                {t("nuvatr.how.titleA")} <em className="italic text-muted-foreground">{t("nuvatr.how.titleEm")}</em>
              </>
            }
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-px bg-border hairline rounded-xl overflow-hidden">
            {steps.map((s) => (
              <div key={s.n} className="bg-card p-8">
                <div className="font-mono-eyebrow text-muted-foreground">{s.n}</div>
                <h3 className="mt-3 font-serif text-[22px] leading-tight">{s.title}</h3>
                <p className="mt-2 text-[14.5px] leading-relaxed text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <div className="font-mono-eyebrow text-muted-foreground">{t("nuvatr.cta.eyebrow")}</div>
          <h2 className="mt-3 font-serif text-[40px] sm:text-[56px] leading-[1.02] tracking-[-0.025em]">
            {t("nuvatr.cta.titleA")} <em className="italic text-muted-foreground">{t("nuvatr.cta.titleEm")}</em>
          </h2>
          <p className="mt-5 text-[16.5px] text-muted-foreground leading-[1.6] max-w-xl mx-auto">
            {t("nuvatr.cta.sub")}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/settings"
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90 transition"
            >
              {t("nuvatr.cta.primary")} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <a
              href="https://nuvatr.com"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 rounded-full hairline px-5 py-3 text-[14px] font-medium text-foreground hover:bg-cream transition"
            >
              {t("nuvatr.cta.secondary")} <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
          <p className="mt-4 text-[12.5px] text-muted-foreground">
            {t("nuvatr.cta.footnote")}
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
